import { Injectable, inject, signal } from '@angular/core';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { defer, iif, Observable, of } from 'rxjs';

import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';

import { OfficeService } from '@pages/office/services/office.service';
import { OfficeToolsEvents } from '@pages/office/models/office-tools-events.model';
import { commonCatchErrorHandler } from '@pages/office/office.constants';
import { ComponentProperties } from '@core/types/component-properties.type';
import { NotificationsService } from '@core/services/notifications.service';
import { UtilityService } from '@core/services/utility.service';
import {
  ModalFormTypeEnum,
  ModalFormMetadata,
  FormModalMetadata,
  ConfirmationModalMetadata,
  IBaseEventArgs,
} from '../../models/modal-form-metadata.model';
import { FormEventsModalComponent } from './form-events-modal/form-events-modal.component';
import { ConfirmationEventsModalComponent } from './form-events-modal/confirmation-events-modal.component';
import { FormEventsModalConfig } from './models/form-events-modal-config.model';
import { OfficeFieldsValuesObj } from '../../models/office-data.model';
import { OFFICE_TOOLS_FORM_EVENT_HANDLERS } from '../office-form/event-handlers';
import { OfficeToolsFormName } from '../office-form/enums/form-name.enum';

@Injectable({ providedIn: 'root' })
export class OfficeToolsEventsService {
  public formEvents = new Set<OfficeToolsEvents>([]);
  public readonly modalApplyFields$ = signal<Record<number, unknown>>({});

  private modalService = inject(BsModalService);
  private officeService = inject(OfficeService);
  private notificationsService = inject(NotificationsService);
  private utilityService = inject(UtilityService);
  private commonCatchErrorHandler = commonCatchErrorHandler();

  public preEventsBridge(config: FormEventsModalConfig): Observable<boolean> {
    this.modalApplyFields$.set({});

    const result = this.handleFormSpecificEvents(config);
    if (result?.isCanceled) {
      result.error && this.notificationsService.showError(result.error); // TODO: show error/info depending on resultMessage severity
      return of(true);
    }

    return iif(
      () => this.formEvents.has(config.eventName),
      defer(() => this.fetchAndProcessModal(config)),
      of(false)
    );
  }

  private handleFormSpecificEvents(config: FormEventsModalConfig): IBaseEventArgs | null {
    const formEventHandlers = OFFICE_TOOLS_FORM_EVENT_HANDLERS[config.formName as OfficeToolsFormName];
    if (!formEventHandlers) return null;
    const formSpecificHandler = formEventHandlers[config.eventName];
    return formSpecificHandler ? formSpecificHandler.handle(config) : null;
  }

  private fetchAndProcessModal(config: FormEventsModalConfig): Observable<boolean> {
    const spinnerState = { isOpen: true };
    this.utilityService.openModal();
    return this.officeService.getModalMetadata(config).pipe(
      switchMap(initialForm => {
        if (initialForm?.isCanceled) {
          initialForm?.error && this.notificationsService.showError(initialForm.error);
          return of(true);
        }
        if (!initialForm) return of(false);
        initialForm.applyFields && this.mergeApplyFields(initialForm.applyFields);
        this.closeSpinner(spinnerState);
        return this.runModalChain(config, initialForm);
      }),
      catchError(this.commonCatchErrorHandler),
      finalize(() => this.closeSpinner(spinnerState))
    );
  }

  private runModalChain(config: FormEventsModalConfig, form: ModalFormMetadata): Observable<boolean> {
    const isConfirmation = form.modalType === ModalFormTypeEnum.Confirmation;
    const modalRef = isConfirmation ? this.showConfirmation(form) : this.showFormModal(form);
    const content = modalRef.content as { onConfirm: Observable<OfficeFieldsValuesObj | boolean | null> };
    return content.onConfirm.pipe(
      take(1),
      switchMap(result => {
        const spinnerState = { isOpen: true };
        this.utilityService.openModal();
        const confirmed = isConfirmation ? Boolean(result) : null;
        const modalFormData = !isConfirmation ? (result as OfficeFieldsValuesObj) : null;
        return this.officeService.postModalForm(config, confirmed, modalFormData, form.nextStep).pipe(
          switchMap(response => {
            this.closeSpinner(spinnerState);
            if (response?.isCanceled) {
              response?.error && this.notificationsService.showError(response.error);
              return of(true);
            }
            response?.applyFields && this.mergeApplyFields(response?.applyFields);
            if (response?.nextStep) {
              return this.runModalChain(config, response);
            }
            if (response?.resultMessage) {
              this.notificationsService.showSuccess(response.resultMessage);
            }
            return of(false);
          }),
          catchError(this.commonCatchErrorHandler),
          finalize(() => this.closeSpinner(spinnerState))
        );
      })
    );
  }

  private showFormModal(model: FormModalMetadata): BsModalRef<FormEventsModalComponent> {
    const initialState: ComponentProperties<Partial<FormEventsModalComponent>> = {
      title: model.title,
      officeFields: model.items ?? [],
    };
    const config: ModalOptions = { initialState, ignoreBackdropClick: true, keyboard: false, class: 'modal-dialog-centered' };
    return this.modalService.show(FormEventsModalComponent, config);
  }

  private showConfirmation(model: ConfirmationModalMetadata): BsModalRef<ConfirmationEventsModalComponent> {
    const initialState: ComponentProperties<Partial<ConfirmationEventsModalComponent>> = {
      title: model.title,
      prompt: model.confirmationPrompt,
    };
    const config: ModalOptions = { initialState, ignoreBackdropClick: true, keyboard: false, class: 'modal-dialog-centered' };
    return this.modalService.show(ConfirmationEventsModalComponent, config);
  }

  private mergeApplyFields(fields: Record<number, unknown>): void {
    this.modalApplyFields$.update(prev => ({ ...prev, ...fields }));
  }

  private closeSpinner(spinnerState: { isOpen: boolean }): void {
    if (spinnerState.isOpen) {
      this.utilityService.closeModal();
      spinnerState.isOpen = false;
    }
  }
}
